import { FC, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { TextField } from '../../../../../atoms/text-field/text-field';
import { Typography } from '../../../../../atoms/typography/typography';
import { StyledDiv } from '../../../../../common/components/styled-div';
import { StyledFixedButtonWrapper } from '../../../../../common/components/styled-fixed-button-wrapper/styled-fixed-button-wrapper';
import { Button } from '../../../../../atoms/button/button';
import { Spacing } from '../../../../../atoms/spacing/spacing';
import { AlertDialog } from '../../../../../molecules/dialogs-common/alert-popup';
import {
  Form,
  FormField,
  FormItem,
  FormControl,
} from '../../../../../molecules/form/form';

interface ListSectionProps {
  title: string;
  items: string[];
}
const ListSection: FC<ListSectionProps> = ({ title, items }) => (
  <StyledDiv flexDirection="column">
    <Typography variant="textBodyMediumNeutral10">{title}:</Typography>
    <Spacing marginTop="md" />
    {items.map((item, idx) => (
      <Spacing key={`${title}-${idx}`} marginBottom="lg">
        <Typography variant="textBodySmall">{item}</Typography>
      </Spacing>
    ))}
  </StyledDiv>
);

const feedbackSchema = z.object({
  feedbackText: z
    .string()
    .min(1, 'Feedback is required.')
    .min(10, 'Feedback must be at least 10 characters long.'),
});

type FeedbackFormValues = z.infer<typeof feedbackSchema>;

interface ObservationSectionFeedbackProps {
  teacherName: string;
  feedbackText?: string;
  strengths: string[];
  weaknesses: string[];
  onSubmit: (data: FeedbackFormValues) => void;
  onViewSummary: (feedbackText: string) => void;
  disabled?: boolean;
}

export const ObservationSectionFeedback: FC<
  ObservationSectionFeedbackProps
> = ({
  teacherName,
  feedbackText = '',
  strengths,
  weaknesses,
  onSubmit,
  onViewSummary,
  disabled = false,
}) => {
  const methods = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { feedbackText },
  });

  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [formData, setFormData] = useState<FeedbackFormValues | null>(null);

  /**
   * Instead of submitting immediately, we capture the data and show a confirmation popup.
   * Observations are final and cannot be edited once completed.
   */
  const handleSubmit: SubmitHandler<FeedbackFormValues> = (data) => {
    setFormData(data);
    setShowConfirmPopup(true);
  };

  const handleConfirmFinish = () => {
    setShowConfirmPopup(false);
    if (formData) {
      onSubmit(formData);
    }
  };

  return (
    <Form {...methods}>
      <AlertDialog
        title="Complete Observation?"
        description="Once you complete this observation, you won't be able to edit it again."
        showPopup={showConfirmPopup}
        primaryButtonText="Complete"
        secondaryButton={true}
        secondaryButtonText="Cancel"
        onPrimaryButtonClick={handleConfirmFinish}
        onSecondaryButtonClick={() => setShowConfirmPopup(false)}
        onClosePopup={() => setShowConfirmPopup(false)}
      />
      <form onSubmit={methods.handleSubmit(handleSubmit)} noValidate>
        <Typography variant="textBodyMediumNeutral10">
          Write actionable feedback to the teacher.
        </Typography>
        <Spacing marginTop="md" marginBottom="xxxxxl">
          <FormField
            control={methods.control}
            name="feedbackText"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <TextField
                    variant="outlined"
                    label="Feedback"
                    type="textarea"
                    placeholder="Write your feedback here..."
                    value={field.value}
                    onInput={(e) => field.onChange(e.target.value)}
                    name={field.name}
                    error={!!fieldState.error}
                    errorText={fieldState.error?.message}
                    rows={12}
                    cols={100}
                    disabled={disabled}
                    readOnly={disabled}
                    data-cy="feedback-textarea"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </Spacing>

        <StyledFixedButtonWrapper bottom={0}>
          <StyledDiv flexDirection="column" gap={8}>
            <Button
              variant="text"
              type="button"
              onClick={() => onViewSummary(methods.getValues('feedbackText'))}
              data-cy="view-summary-button"
            >
              View Observation Summary
            </Button>
            <Button type="submit" disabled={disabled} data-cy="done-button">
              Done
            </Button>
          </StyledDiv>
        </StyledFixedButtonWrapper>
      </form>
    </Form>
  );
};
